// libraries
import React, { useMemo, useState, useCallback, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

export type NewsView =
	| 'two-column'
	| 'three-column'
	| 'one-right'
	| 'one-left'
	| 'commentary'

export type NewsBackground = 'white' | 'light' | 'dark'

export interface NewsArticle {
	/** Unique identifier used for deduplication across News instances */
	id?: string
	title: string
	excerpt?: string
	date?: string
	imageSrc?: string
	imageAlt?: string
	author?: string
	link?: string
	linkText?: string
	category?: string
}

export interface NewsProps {
	/** Visual presentation */
	view?: NewsView
	/** Section background theme */
	background?: NewsBackground
	/** Number of rows to render (where applicable) */
	rows?: number
	articles?: NewsArticle[]
	heading?: string
	readMore?: {
		link: string
		linkText: string
	}
	loadMore?: {
		url: string
		text: string
	}
}

type ArticleWithKey = NewsArticle & { __key: string }

const buildArticleKey = (article: NewsArticle, index: number): string => {
	if (isNonWhitespaceString(article.id)) {
		return article.id.trim()
	}
	if (isNonWhitespaceString(article.link)) {
		return article.link.trim()
	}
	if (isNonWhitespaceString(article.title)) {
		const suffix = isNonWhitespaceString(article.date) ? article.date!.trim() : index
		return `${article.title}-${suffix}`
	}
	return `news-item-${index}`
}

const renderImage = (article: NewsArticle, className: string) => {
	if (isNonWhitespaceString(article.imageSrc)) {
		return (
			<div className={className}>
				<img src={article.imageSrc} alt={article.imageAlt || ''} />
			</div>
		)
	}

	return <div className={clsx(className, `${className}--placeholder`)} aria-hidden='true' />
}

const renderArticleBody = (article: NewsArticle) => (
	<div className='news__item-body'>
		{isNonWhitespaceString(article.title) && <h3 className='news__item-title'>{article.title}</h3>}
		{(isNonWhitespaceString(article.date) || isNonWhitespaceString(article.category)) && (
			<div className='news__item-meta'>
				{isNonWhitespaceString(article.date) && <span className='news__item-date'>{article.date}</span>}
				{isNonWhitespaceString(article.category) && <span className='news__item-tag'>{article.category}</span>}
			</div>
		)}
		{isNonWhitespaceString(article.excerpt) && (
			<p
				className='news__item-excerpt'
				dangerouslySetInnerHTML={{ __html: article.excerpt }}
				suppressHydrationWarning
			/>
		)}

	</div>
)

const wrapWithLink = (article: NewsArticle, className: string, children: React.ReactNode) => {
	if (isNonWhitespaceString(article.link)) {
		return (
			<a className={className} href={article.link}>
				{children}
			</a>
		)
	}
	return <div className={className}>{children}</div>
}

const renderGridItem = (article: ArticleWithKey) => (
	<article key={article.__key} className='news__item'>
		{wrapWithLink(
			article,
			'news__item-link',
			<>
				{renderImage(article, 'news__item-image')}
				{renderArticleBody(article)}
			</>
		)}
	</article>
)

const renderFeatureItem = (article: ArticleWithKey, reverse?: boolean) => (
	<article key={article.__key} className={clsx('news__feature', reverse && 'news__feature--reverse')}>
		{wrapWithLink(
			article,
			'news__feature-link',
			<>
				{renderImage(article, 'news__feature-media')}
				<div className='news__feature-body'>{renderArticleBody(article)}</div>
			</>
		)}
		
	</article>
)

const renderCommentaryItem = (article: ArticleWithKey) => (
	<article key={article.__key} className='news__commentary'>
		<div className='news__commentary-line' aria-hidden='true' />

		<div className='news__commentary-avatar'>{renderImage(article, 'news__commentary-avatar-image')}</div>
		{wrapWithLink(
			article,
			'news__commentary-content',
			<div className='news__commentary-content-inner '>
				{isNonWhitespaceString(article.title) && <h3 className='news__commentary-title'>{article.title}</h3>}
				{isNonWhitespaceString(article.excerpt) && <p className='news__commentary-excerpt'>{article.excerpt}</p>}
			</div>
		)}

	</article>
)

export default function News({
	view = 'two-column',
	background = 'white',
	rows = 1,
	articles = [],
	heading,
	readMore,
	loadMore
}: NewsProps) {
	const [allArticles, setAllArticles] = useState<NewsArticle[]>(articles || [])
	const [nextPageUrl, setNextPageUrl] = useState<string | undefined>(loadMore?.url)
	const [isLoading, setIsLoading] = useState(false)

	// Update articles when prop changes
	useEffect(() => {
		setAllArticles(articles || [])
		setNextPageUrl(loadMore?.url)
	}, [articles, loadMore?.url])

	const normalizedItems = useMemo(() => {
		const safeArticles = Array.isArray(allArticles) ? allArticles : []

		return safeArticles
			.filter((article): article is NewsArticle => Boolean(article && isNonWhitespaceString(article.title)))
			.map((article, index) => ({
				...article,
				__key: buildArticleKey(article, index)
			}))
	}, [allArticles])

	const handleLoadMore = useCallback(async () => {
		if (isLoading || !nextPageUrl) return

		setIsLoading(true)
		try {
			const response = await fetch(nextPageUrl)
			if (!response.ok) {
				throw new Error('Failed to load more articles')
			}
			const data = await response.json()

			if (data.articles && Array.isArray(data.articles)) {
				setAllArticles((prevArticles) => [...prevArticles, ...data.articles])
			}
			setNextPageUrl(data.nextPageUrl)
		} catch (error) {
			console.error('Error loading more articles:', error)
		} finally {
			setIsLoading(false)
		}
	}, [isLoading, nextPageUrl])

	if (!normalizedItems.length) {
		return null
	}

	return (
		<Section
			className={clsx('news', `news--${background}`, `news--${view}`)}
			paddingTop='medium'
			paddingBottom='medium'
		>
			<div className='news__container container'>
				{isNonWhitespaceString(heading) && (
					<div className='news__header'>
						<h2 className='news__heading'>{heading}</h2>
					</div>
				)}

				{view === 'commentary' && normalizedItems.length > 0 && renderCommentaryItem(normalizedItems[0])}
				{(view === 'one-right' || view === 'one-left') && (
					<div className='news__feature-list'>
						{normalizedItems.map((item) => renderFeatureItem(item, view === 'one-left'))}
					</div>
				)}
				{(view === 'two-column' || view === 'three-column') && (
					<div
						className={clsx(
							'news__grid',
							view === 'two-column' && 'news__grid--two',
							view === 'three-column' && 'news__grid--three'
						)}
					>
						{normalizedItems.map((item) => renderGridItem(item))}
					</div>
				)}
				{readMore && (

					<a href={readMore.link} className='news__section-link'>
						{readMore.linkText}
						<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path fillRule="evenodd" clipRule="evenodd" d="M30.795 17.205C31.0057 17.4159 31.124 17.7019 31.124 18C31.124 18.2981 31.0057 18.584 30.795 18.795L21.795 27.795C21.692 27.9055 21.5678 27.9942 21.4298 28.0557C21.2918 28.1171 21.1428 28.1502 20.9918 28.1529C20.8407 28.1555 20.6907 28.1278 20.5506 28.0712C20.4105 28.0146 20.2833 27.9304 20.1765 27.8235C20.0696 27.7167 19.9854 27.5895 19.9288 27.4494C19.8722 27.3093 19.8445 27.1593 19.8471 27.0082C19.8498 26.8572 19.8829 26.7082 19.9443 26.5702C20.0058 26.4322 20.0945 26.308 20.205 26.205L27.285 19.125H6.00001C5.70164 19.125 5.41549 19.0065 5.20452 18.7955C4.99354 18.5845 4.87501 18.2984 4.87501 18C4.87501 17.7016 4.99354 17.4155 5.20452 17.2045C5.41549 16.9935 5.70164 16.875 6.00001 16.875H27.285L20.205 9.79499C20.0945 9.69199 20.0058 9.56779 19.9443 9.42979C19.8829 9.29179 19.8498 9.14282 19.8471 8.99177C19.8445 8.84072 19.8722 8.69067 19.9288 8.55059C19.9854 8.41051 20.0696 8.28326 20.1765 8.17643C20.2833 8.0696 20.4105 7.98538 20.5506 7.9288C20.6907 7.87222 20.8407 7.84443 20.9918 7.8471C21.1428 7.84976 21.2918 7.88283 21.4298 7.94431C21.5678 8.0058 21.692 8.09446 21.795 8.20499L30.795 17.205Z" fill="#5C0120" />
						</svg>

					</a>

				)}

				{nextPageUrl && (
					<div className='news__load-more'>
						<Button onClick={handleLoadMore} style="solid" color="primary" small disabled={isLoading}>
							{loadMore?.text || 'Last flere'}
						</Button>
					</div>
				)}
			</div>
		</Section>
	)
}

